import { ApolloClient, gql, Reference } from '@apollo/client'
import {
  CREATE_ORDER,
  RETRY_ORDER_PAYMENT,
  UPDATE_ORDER_STATUS
} from '../graphql/mutations'
import { 
  GET_ORDERS, 
  GET_ORDER
} from '../graphql/queries'
import { 
  Order, 
  CreateOrderData, 
  OrderFilters, 
  OrderStatus
} from '../types/order'

export class OrderService {
  constructor(private apolloClient: ApolloClient<any>) {}

  async createOrder(orderData: CreateOrderData): Promise<Order> {
    try {
      // Payment is now required in orderData
      // The API will automatically process the payment:
      // - Cash: Immediately completed, order auto-confirmed if fully paid
      // - Card: Processed via CCV terminal, status updated via polling
      // - Online: Not yet implemented (will throw error)
      const response = await this.apolloClient.mutate({
        mutation: CREATE_ORDER,
        variables: { orderData },
        // Keep order lists in sync without requiring a full app reload
        update: (cache, { data }) => {
          const newOrder = data?.createOrder
          if (!newOrder) return

          // Write the new order into the cache and get a reference
          const newOrderRef = cache.writeFragment({
            data: newOrder,
            fragment: gql`
              fragment NewOrder on Order {
                id
                order_number
                status
                order_type
                customer_name
                total_cents
                created_at
              }
            `
          })

          // Prepend the new order to unfiltered order lists (filters: {} or no filters)
          cache.modify({
            fields: {
              orders(existingOrderRefs: readonly any[] | Reference | undefined, { readField, args }: any) {
                // Handle case where value might be a Reference or undefined
                if (!existingOrderRefs || !Array.isArray(existingOrderRefs)) {
                  return existingOrderRefs
                }

                const hasFilters =
                  args?.filters &&
                  Object.keys(args.filters).some((key) => args.filters[key] != null)

                // Only auto-insert into queries without server-side filters;
                // filtered views rely on their own polling/refetch.
                if (hasFilters) {
                  return existingOrderRefs
                }

                const alreadyExists = existingOrderRefs.some(
                  (ref: any) => readField('id', ref) === newOrder.id
                )
                if (alreadyExists) {
                  return existingOrderRefs
                }

                return [newOrderRef, ...existingOrderRefs]
              }
            }
          })
        }
      })

      return response.data!.createOrder
    } catch (error) {
      throw this.handleOrderError(error)
    }
  }

  /**
   * Pay an EXISTING unpaid POS order again after a declined or cancelled card
   * (API 2026-10-08, payment-integration-guide → "Paying an existing order again"):
   * no second order, nothing for staff to void. `payment` is the same input
   * createOrder takes (method + CCV terminal fields); the amount is the server's —
   * the order's open balance — so amount_cents is ignored. The returned order
   * carries the new payment, handled exactly like createOrder's. Every refusal
   * ("Payment retry refused: …", "Payment method … is not allowed", "Reward is no
   * longer attached …") is a plain message the checkout shows verbatim.
   */
  async retryOrderPayment(orderId: number, payment: CreateOrderData['payment']): Promise<Order> {
    try {
      const response = await this.apolloClient.mutate({
        mutation: RETRY_ORDER_PAYMENT,
        variables: { orderId, payment },
      })
      return response.data!.retryOrderPayment
    } catch (error) {
      throw this.handleOrderError(error)
    }
  }

  async updateOrderStatus(orderId: number, status: OrderStatus): Promise<Order> {
    try {
      const response = await this.apolloClient.mutate({
        mutation: UPDATE_ORDER_STATUS,
        variables: { id: orderId, status }
      })

      return response.data.updateOrderStatus
    } catch (error) {
      throw this.handleOrderError(error)
    }
  }

  async getOrders(filters?: OrderFilters): Promise<Order[]> {
    try {
      const response = await this.apolloClient.query({
        query: GET_ORDERS,
        variables: { filters },
        fetchPolicy: 'cache-first'
      })

      return response.data.orders
    } catch (error) {
      throw this.handleOrderError(error)
    }
  }

  async getOrder(orderId: number): Promise<Order> {
    try {
      const response = await this.apolloClient.query({
        query: GET_ORDER,
        variables: { id: orderId },
        fetchPolicy: 'cache-first'
      })

      return response.data.order
    } catch (error) {
      throw this.handleOrderError(error)
    }
  }




  private handleOrderError(error: any): Error {
    // Messages the checkout UI must read verbatim, preserved before the
    // catch-all below rewrites every unknown message into a generic one:
    //  - Curry Me Club (§7): a counter code that is used/expired, or a reward
    //    that no longer applies — CheckoutModal shows these as-is.
    //  - the product sold-out message ("… is no longer available") — CheckoutModal
    //    greys the tile and offers Retry on it; a generic rewrite kills that path.
    //  - the retryOrderPayment refusals ("Payment retry refused: …", "Payment retry is
    //    only available …", "Payment method 'X' is not allowed …") — each names what
    //    the cashier must do (cancel the running sale, nothing left to pay, …).
    const raw = error.graphQLErrors?.[0]?.message ?? error.message ?? ''
    if (
      /^(Counter code|Reward |Payment retry|Payment method)/.test(raw) ||
      raw.includes('is no longer available')
    ) {
      return new Error(raw)
    }

    if (error.message?.includes('Staff user not found')) {
      return new Error('Staff user not found. Please log in again.')
    }
    
    if (error.message?.includes('Product variant not found')) {
      return new Error('One or more products are no longer available.')
    }
    
    if (error.message?.includes('Insufficient permissions')) {
      return new Error('You do not have permission to perform this action.')
    }
    
    return new Error('An unexpected error occurred. Please try again.')
  }
}
